import toBarEchartOption from "src/components/Charts/Evolutive/echarts/BarChart/toEchartOption";
import { toEchartData } from "src/components/Charts/Evolutive/echarts/dataProcessor";
import toLineEchartOption from "src/components/Charts/Evolutive/echarts/LineChart/toEchartOption";
import { LINE, BAR } from "src/components/Charts/types";
import {
  addBreackLines,
  addBreakLinesToSingleString,
} from "src/util/formatters/stringNormalizer";
import { normalizarRotulosDuplicados } from "src/util/string/normalizarRotulosDuplicados";

const DEFAULT_MARGIN_BETWEEN_AXIS_AND_LEGEND = 16;
const BREAK_LINES_QUANTITY = 1;
const SAFEST_ESTIMATED_RELATORIO_CHART_CONTAINER_WIDTH = 400;
const SAFEST_ESTIMATED_RELATORIO_LEGEND_CHAR_SIZE = 5;

/**
 * Funcao que produz configuracoes do grafico evolutivos para
 * serem utilizadas no relatorio de resultados
 *
 * @param {chartType} tipo do gráfico. pode ser: 'line' ou 'bar'
 * @param {data} resultados a serem plotados
 * @param {theme} tema utilizado
 */
export function getReportEvolutiveChartEchartOption(chartType, data, theme) {
  const defaultChartType = LINE;
  const tiposPermitidos = [BAR, LINE];

  const type = tiposPermitidos.includes(chartType)
    ? chartType
    : defaultChartType;

  if (type === LINE) {
    return proccessAndNormalizeLineEvolutiveEchartOptions(data, theme);
  }

  return proccessAndNormalizeBarEvolutiveEchartOptions(data, theme);
}

/**
 * Funcao que normaliza/aplica regras especificas para as
 * options do grafico evolutivo utilizado no relatorio
 * de resultados
 *
 * @param {data} resultados a serem plotados
 * @param {echartOptions} options opções do echart com todos os dados/
 * necessarios para plotagem
 */
function proccessAndNormalizeLineEvolutiveEchartOptions(data, theme) {
  const echartOptions = toLineEchartOption(data, theme);

  delete echartOptions.xAxis.axisLabel.rotate;

  const { points } = toEchartData(data);
  const pontosFormatados = normalizarPontosParaRelatorio(points);

  echartOptions.xAxis.data = pontosFormatados;

  return echartOptions;
}

function proccessAndNormalizeBarEvolutiveEchartOptions(data, theme) {
  const echartOptions = toBarEchartOption(data, theme);
  delete echartOptions.xAxis.axisLabel.rotate;

  const [firstDataSetSource] = echartOptions.dataset.source;

  /**
   * Isto indica que nossa forma de setar um dataSet neste gráfico evolutivo mudou;
   * interrompe o processamento
   */
  if (!Array.isArray(firstDataSetSource)) {
    return echartOptions;
  }

  const { points } = toEchartData(data);
  const pontosFormatados = normalizarPontosParaRelatorio(points);

  const pontosNormalizados = normalizarRotulosDuplicados(
    "whitespace",
    ...pontosFormatados
  );

  firstDataSetSource.length = 0;
  firstDataSetSource.push("Pesquisas", ...pontosNormalizados);

  echartOptions.xAxis.axisLabel.margin = DEFAULT_MARGIN_BETWEEN_AXIS_AND_LEGEND;

  return echartOptions;
}

/**
 * Basicamente realiza uma série de cálculos estimativos (com margem de segurança)
 * para identificar quais conjuntos de legendas do eixo x (points) devem ser normalizados;
 * 
 * Steps:
 * - Primeiro estima-se quantos caracteres cabem em uma categoria no eixo x;
 * - Em seguida, verifica se o ponto se enquadra na estimativa calculada anteriormente;
 * - Caso o ponto se enquadre na categoria, nenhuma normalização é aplicada no ponto
 * - Caso contrário, aplica uma quebra de linha a cada N caracteres identificados como
 * transbordadores;
 * 
 * @param points 
 * @returns 
 */
function normalizarPontosParaRelatorio(points) {
  const comprimentoDisponivelPorCategoria =
    SAFEST_ESTIMATED_RELATORIO_CHART_CONTAINER_WIDTH / points.length;

  const estimativaQtdCaracteresDisponiveisPorCategoria =
    comprimentoDisponivelPorCategoria /
    SAFEST_ESTIMATED_RELATORIO_LEGEND_CHAR_SIZE;

  const pontosFormatados = [];

  for (let i = 0; i < points.length; i++) {
    const pontoAtual = points[i];
    const comprimentoEstimadoAtual =
      pontoAtual.length * SAFEST_ESTIMATED_RELATORIO_LEGEND_CHAR_SIZE;

    if (
      comprimentoEstimadoAtual < estimativaQtdCaracteresDisponiveisPorCategoria
    ) {
      pontosFormatados.push([pontoAtual]);
      continue;
    }

    const palavras = pontoAtual.split(" ");
    const palavrasFormatadas = [];

    for (let j = 0; j < palavras.length; j++) {
      const comprimentoEstimadoPalavra =
        palavras[j].length * SAFEST_ESTIMATED_RELATORIO_LEGEND_CHAR_SIZE;

      if (
        comprimentoEstimadoPalavra <
        estimativaQtdCaracteresDisponiveisPorCategoria
      ) {
        palavrasFormatadas.push(palavras[j]);
        continue;
      }

      const palavraFormatada = addBreakLinesToSingleString(
        palavras[j],
        estimativaQtdCaracteresDisponiveisPorCategoria,
        BREAK_LINES_QUANTITY
      );

      palavrasFormatadas.push(palavraFormatada);
    }

    const pontoComPalavrasFormatadas = palavrasFormatadas.join(" ");

    pontosFormatados.push(
      addBreackLines(
        [pontoComPalavrasFormatadas],
        estimativaQtdCaracteresDisponiveisPorCategoria,
        BREAK_LINES_QUANTITY
      )
    );
  }

  const pontosFormatadosFlat = pontosFormatados.flat();

  return pontosFormatadosFlat;
}
